/**
 * Optical lens group classifier derived from the "groupe" sheet in
 * stock_Tebessa_all_include.xlsx.
 *
 * Sub-group (/2, /4, /6, /8, /10, /12+) is determined by |sph|:
 *   [0,2) → /2   [2,4) → /4   [4,6) → /6   [6,8) → /8   [8,10) → /10   [10,12+] → /12+
 *
 * Main group is determined by cylinder:
 *   cyl == 0      → G0 (sphérique)
 *   cyl in (0,2]  → G2 (torique)
 *   cyl in (2,4]  → G4 (torique fort)
 */

const SPH_SUBGROUPS = [
  [0,   2,  '2'],
  [2,   4,  '4'],
  [4,   6,  '6'],
  [6,   8,  '8'],
  [8,  10,  '10'],
  [10, 12.25, '12+'],
];

function getSubGroup(sph) {
  const absSph = Math.abs(sph);
  for (const [min, max, label] of SPH_SUBGROUPS) {
    if (absSph >= min && absSph < max) return label;
  }
  return null;
}

function getMainGroup(cyl) {
  const absCyl = Math.abs(cyl);
  if (absCyl === 0)               return { group: 'G0', type: 'sphérique' };
  if (absCyl > 0 && absCyl <= 2) return { group: 'G2', type: 'torique' };
  if (absCyl > 2 && absCyl <= 4) return { group: 'G4', type: 'torique fort' };
  return { group: 'G?', type: 'hors plage cyl' };
}

export function getLensGroup({ sph, cyl = 0 }) {
  const subGroup = getSubGroup(sph);
  const { group, type } = getMainGroup(cyl);

  if (subGroup === null) {
    return {
      group,
      subGroup: 'hors plage',
      label: `${group}/hors plage`,
      type,
      valid: false,
      message: `SPH ±${Math.abs(sph)} dépasse la plage standard (max ±12). Commande Rx nécessaire.`,
    };
  }

  const label = `${group}/${subGroup}`;
  return {
    group,
    subGroup,
    label,
    type,
    valid: true,
    message: `Groupe ${label} — ${type}`,
  };
}

export function getLensGroupLabel(sph, cyl = 0) {
  const result = getLensGroup({ sph, cyl });
  return result.valid ? result.label : null;
}

export function getAllGroupLabels() {
  const groups = ['G0', 'G2', 'G4'];
  const subs   = ['2', '4', '6', '8', '10', '12+'];
  return groups.flatMap(g => subs.map(s => `${g}/${s}`));
}

/**
 * Transpose between (+) cyl and (−) cyl prescription forms.
 *   new SPH = old SPH + old CYL
 *   new CYL = -old CYL
 *   new AXE = old AXE >= 90 ? old AXE - 90 : old AXE + 90
 * Returns string fields (formatted) so they can be put straight into form inputs.
 */
export function transposeCylinder({ sphere, cylinder, axis }) {
  const s = parseFloat(sphere);
  const c = parseFloat(cylinder);
  const a = parseFloat(axis);
  if (Number.isNaN(c)) return { sphere, cylinder, axis };

  const newSph = (Number.isNaN(s) ? 0 : s) + c;
  const newCyl = -c;
  let newAxisStr = '';
  if (!Number.isNaN(a)) {
    const na = a >= 90 ? a - 90 : a + 90;
    newAxisStr = String(Math.round(na));
  }
  return {
    sphere: newSph.toFixed(2),
    cylinder: newCyl.toFixed(2),
    axis: newAxisStr,
  };
}

/** Spherical equivalent: SE = SPH + CYL/2. Returns null if SPH is missing. */
export function computeSphericalEquivalent(sphere, cylinder) {
  const s = parseFloat(sphere);
  if (Number.isNaN(s)) return null;
  const c = parseFloat(cylinder) || 0;
  return s + c / 2;
}

/**
 * Like getLensGroup, but if the exact (sph, cyl) combination is invalid
 * (e.g. sph out of range), retry with the spherical equivalent and cyl=0.
 * Helps surface a usable group for borderline prescriptions.
 */
export function getLensGroupWithSEFallback({ sph, cyl = 0 }) {
  const primary = getLensGroup({ sph, cyl });
  if (primary.valid) return primary;
  const se = sph + (cyl || 0) / 2;
  const fallback = getLensGroup({ sph: se, cyl: 0 });
  if (fallback.valid) {
    return {
      ...fallback,
      message: `SE ${se.toFixed(2)} → Groupe ${fallback.label} (${fallback.type})`,
      usedSE: true,
    };
  }
  return primary;
}
